import React, { useState, useEffect, useRef } from 'react'
import { lockBodyScroll, unlockBodyScroll } from '@helpers/bodyscroll'
import PropTypes from 'prop-types'
import { motion, AnimatePresence } from 'framer-motion'
import useStaticNavigationQuery from '@hooks/useStaticNavigationQuery'
import { styled, Paper } from '@mui/material'
import NavItem from './NavItem'
import Megamenu from './Megamenu'

const Nav = styled('nav')`
  display: flex;
  position: relative;
  width: 100%;
  padding: 0 4px;
  border-top: solid 1px rgba(0, 0, 0, 0.1);
  justify-content: center;
  ${p => p.theme.breakpoints.down('md')} {
    display: none;
  }
  margin: 0 auto;
`

const NavGutter = styled('div')`
  width: 100%;
  flex-grow: 1;
`

const ListWrapper = styled('div')`
  width: 100%;
  flex: 1;
`

const List = styled('ul')`
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  width: max-content;
  margin: 0;
  padding: 0;
  list-style: none;
`

const Popover = styled(Paper)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  margin: 0 auto;
  display: flex;
  width: 100%;
  background-color: ${p => p.theme.palette.background.paper};
  border-radius: 0 0 8px 8px;
  padding: 0;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
`

const Navigation = ({ onSetBackdrop }) => {
  const navigation = useStaticNavigationQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [isHover, setIsHover] = useState(false)
  const [activeSection, setActiveSection] = useState(null)
  const [isFirstOpenTab, setIsFirstOpenTab] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const lastFocusedNavItemRef = useRef(null)
  const displayMegamenu = Boolean(isOpen && activeSection?.NavLinks?.length)
  const timeoutIsHovering = useRef(null)
  const timeoutUnhovering = useRef(null)
  const timeoutIsClosing = useRef(null)
  // This handles the hover over delay that opens the megamenu
  useEffect(() => {
    clearTimeout(timeoutIsHovering.current)
    clearTimeout(timeoutUnhovering.current)

    if (isHover) {
      timeoutIsHovering.current = setTimeout(() => {
        setIsOpen(true)
        setIsFirstOpenTab(true)
      }, 259)
    } else {
      timeoutUnhovering.current = setTimeout(() => {
        setIsOpen(false)
      }, 259)
    }

    return () => {
      if (timeoutIsHovering.current) clearTimeout(timeoutIsHovering.current)
      if (timeoutUnhovering.current) clearTimeout(timeoutUnhovering.current)
    }
  }, [isHover])

  // While the megamenu is closing, there's a 200ms transition.
  // We don't want the user to be able to hover over the fading meganav to reopen it.
  // This timeout tracks the closing state for this purpose.
  useEffect(() => {
    clearTimeout(timeoutIsClosing.current)

    if (!isOpen) {
      setIsClosing(true)
      timeoutIsClosing.current = setTimeout(() => {
        setIsClosing(false)
      }, 200)
    }

    return () => {
      if (timeoutIsClosing.current) clearTimeout(timeoutIsClosing.current)
    }
  }, [isOpen])

  // Manages transition of the firstOpenTab
  useEffect(() => {
    if (isFirstOpenTab) {
      setIsFirstOpenTab(false)
    }
  }, [activeSection, isFirstOpenTab])

  // Handles body scroll lock and analytics
  useEffect(() => {
    if (displayMegamenu) {
      lockBodyScroll()
      onSetBackdrop(true)
      if (typeof window !== 'undefined') {
        const primaryLink = activeSection?.PrimaryLink?.Link
        window.dataLayer.push({
          category: 'navigation',
          action: 'open menu',
          label: primaryLink?.DisplayText,
        })
      }
    } else {
      unlockBodyScroll()
      onSetBackdrop(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMegamenu])

  const onKeyDown = e => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      lastFocusedNavItemRef.current?.focus()
    }
  }

  return (
    <Nav
      className="desktop-only"
      id="primary-nav-desktop"
      role="navigation"
      aria-label="Primary Navigation"
      onKeyDown={onKeyDown}
    >
      <NavGutter />
      <ListWrapper
        onMouseEnter={() => {
          if (!isClosing) setIsHover(true)
        }}
        onMouseLeave={() => setIsHover(false)}
      >
        <List
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => {
            if (!isOpen) {
              setIsHover(false)
            }
          }}
        >
          {navigation.map(({ HeaderNavLinks }) => {
            const contentID = HeaderNavLinks?.id
            const isActive = activeSection?.id === HeaderNavLinks?.id

            return (
              <NavItem
                key={contentID}
                isSelected={isActive && displayMegamenu}
                disableTabbing={!isActive && displayMegamenu}
                isFirstOpenTab={isFirstOpenTab}
                navigationLink={HeaderNavLinks}
                setActiveSection={section => {
                  if (section) setActiveSection(section)
                }}
                setIsOpen={open => {
                  setIsOpen(open)
                }}
                isOpen={isOpen}
                lastFocusedNavItemRef={lastFocusedNavItemRef}
              />
            )
          })}
        </List>
        <AnimatePresence mode="wait">
          {isOpen && !!activeSection?.NavLinks?.length && (
            <Popover
              key="menuitem"
              component={motion.section}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClose={() => setIsOpen(false)}
            >
              <Megamenu data={activeSection} onClose={() => setIsOpen(false)} />
            </Popover>
          )}
        </AnimatePresence>
      </ListWrapper>
      <NavGutter />
    </Nav>
  )
}

Navigation.propTypes = {
  onSetBackdrop: PropTypes.func.isRequired,
}

export default Navigation
